<!-- Standalone page (/petty-cash) gets the shared page header; inside Cash Management the host page has its own -->
<app-page-header
  *ngIf="!embedded"
  title="Petty Cash"
  subtitle="Small expenses paid and cash received at the counter"
  icon="pi pi-wallet"
  (back)="goBackToHome()"
></app-page-header>

<div class="pc-body">
  <!-- Transactions -->
  <article class="pc-card pc-card--table">
    <div class="pc-card__header">
      <h2 class="pc-card__title">Transactions</h2>
      <div class="pc-stats">
        <span class="pc-stat">
          <span class="pc-stat__label">Opening</span>
          <span class="pc-stat__value">{{ openingBalance | currency: "INR" : "symbol" : "1.0-0" }}</span>
        </span>
        <span class="pc-stat pc-stat--accent">
          <span class="pc-stat__label">Current</span>
          <span class="pc-stat__value">{{ currentBalance | currency: "INR" : "symbol" : "1.0-0" }}</span>
        </span>
        <span class="pc-stat">
          <span class="pc-stat__label">Date</span>
          <span class="pc-stat__value">{{ currentDate | date: "shortDate" }}</span>
        </span>
      </div>
    </div>

    <div class="table-container">
      <p-table [value]="pettyCashData" [scrollable]="true" scrollHeight="flex" styleClass="p-datatable-sm pc-table">
        <ng-template pTemplate="header">
          <tr>
            <th class="pc-num">Amount</th>
            <th>Category</th>
            <th>Description</th>
            <th>Cash For</th>
            <th class="pc-num">Balance</th>
            <th>Date</th>
            <th>Type</th>
            <th *ngIf="currentUser?.role != 11" class="pc-actions-col">Actions</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-entry let-i="rowIndex">
          <tr>
            <td class="pc-num">{{ entry.amount | currency: "INR" : "symbol" : "1.0-0" }}</td>
            <td>{{ getExpenseTypeName(entry.expensetype_id) }}</td>
            <td>{{ entry.description }}</td>
            <td>{{ entry.cashFor }}</td>
            <td class="pc-num">{{ getRunningBalance(i) | currency: "INR" : "symbol" : "1.0-0" }}</td>
            <td>{{ entry.createdAt | date: "medium" }}</td>
            <td>
              <span
                class="pc-type"
                [class.pc-type--paid]="entry.type === 'Paid' || entry.type === 'paid'"
                [class.pc-type--received]="!(entry.type === 'Paid' || entry.type === 'paid')"
              >
                {{ entry.type }}
              </span>
            </td>
            <td *ngIf="currentUser?.role != 11" class="pc-actions-col">
              <div class="pc-row-actions">
                <button type="button" class="pc-row-action" (click)="editEntry(entry)" title="Edit" aria-label="Edit entry">
                  <i class="pi pi-pencil"></i>
                </button>
                <button
                  type="button"
                  class="pc-row-action pc-row-action--danger"
                  (click)="deleteEntry(entry.uuid!)"
                  title="Delete"
                  aria-label="Delete entry"
                >
                  <i class="pi pi-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="9">
              <div class="empty-state">
                <i class="pi pi-money-bill empty-state-icon"></i>
                <p>No petty cash transactions to show</p>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </article>

  <!-- Add / edit entry -->
  <aside class="pc-card pc-card--form">
    <div class="pc-card__header">
      <h2 class="pc-card__title">{{ isEditMode ? "Edit Entry" : "Add New Entry" }}</h2>
    </div>
    <form class="pc-form" [formGroup]="pettyCashForm" (ngSubmit)="onSubmit()">
      <div class="pc-form__fields">
        <div class="pc-field">
          <label for="expenseCategory" class="pc-label">Expense Category <span class="pc-req">*</span></label>
          <p-dropdown
            inputId="expenseCategory"
            formControlName="expensetype_id"
            [options]="expenseTypeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select expense category"
            styleClass="pc-control"
          ></p-dropdown>
          <div
            *ngIf="pettyCashForm.get('expensetype_id')?.invalid && pettyCashForm.get('expensetype_id')?.touched"
            class="pc-error"
          >
            Expense category is required
          </div>
        </div>

        <div class="pc-field-row">
          <div class="pc-field">
            <label for="amount" class="pc-label">Amount <span class="pc-req">*</span></label>
            <p-inputNumber
              inputId="amount"
              formControlName="amount"
              mode="currency"
              currency="INR"
              locale="en-IN"
              [min]="1"
              styleClass="pc-control"
            ></p-inputNumber>
            <div *ngIf="pettyCashForm.get('amount')?.invalid && pettyCashForm.get('amount')?.touched" class="pc-error">
              Amount is required and must be greater than 0
            </div>
          </div>
          <div class="pc-field">
            <label for="type" class="pc-label">Type <span class="pc-req">*</span></label>
            <p-dropdown
              inputId="type"
              formControlName="type"
              [options]="typeOptions"
              optionLabel="label"
              optionValue="value"
              styleClass="pc-control"
            ></p-dropdown>
            <div *ngIf="pettyCashForm.get('type')?.invalid && pettyCashForm.get('type')?.touched" class="pc-error">
              Type is required
            </div>
          </div>
        </div>

        <div class="pc-field">
          <label for="description" class="pc-label">Description <span class="pc-req">*</span></label>
          <input
            pInputText
            type="text"
            id="description"
            formControlName="description"
            class="pc-input"
            placeholder="Enter description"
          />
          <div
            *ngIf="pettyCashForm.get('description')?.invalid && pettyCashForm.get('description')?.touched"
            class="pc-error"
          >
            Description is required
          </div>
        </div>

        <div class="pc-field">
          <label for="cashFor" class="pc-label">Cash For <span class="pc-req">*</span></label>
          <input pInputText type="text" id="cashFor" formControlName="cashFor" class="pc-input" placeholder="Enter cash for" />
          <div *ngIf="pettyCashForm.get('cashFor')?.invalid && pettyCashForm.get('cashFor')?.touched" class="pc-error">
            Cash For is required
          </div>
        </div>

        <dl class="pc-meta">
          <div class="pc-meta__row">
            <dt><i class="pi pi-user"></i> Entry by <span class="pc-req">*</span></dt>
            <dd class="pc-meta__strong">{{ pettyCashForm.get("entryBy")?.value || "Current User" }}</dd>
          </div>
          <div class="pc-meta__row">
            <dt><i class="pi pi-clock"></i> Entry timestamp</dt>
            <dd class="pc-meta__mono">{{ currentDate | date: "medium" }}</dd>
          </div>
        </dl>
      </div>

      <div class="pc-form__footer">
        <button type="button" class="premium-btn premium-btn-secondary" (click)="resetForm()">
          <i class="pi pi-refresh"></i>
          Reset
        </button>
        <button type="submit" class="premium-btn premium-btn-primary" [disabled]="pettyCashForm.invalid">
          <i class="pi pi-check"></i>
          {{ isEditMode ? "Update" : "Add" }}
        </button>
      </div>
    </form>
  </aside>
</div>

<p-toast></p-toast>
<p-confirmDialog></p-confirmDialog>
